"use client";

import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface PriceRange {
  min: string;
  max: string;
}

interface PriceFilterProps {
  priceRange: PriceRange;
  onPriceRangeChange: (range: PriceRange) => void;
}

const MIN_PRICE = 0;
const MAX_PRICE = 50_000_000;
const STEP = 1000;

export default function PriceFilter({
  priceRange,
  onPriceRangeChange,
}: PriceFilterProps) {
  const [localRange, setLocalRange] = useState<PriceRange>(priceRange);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Update local state when props change
  useEffect(() => {
    const timer = setTimeout(() => {
      setLocalRange(priceRange);
      setValidationError(null);
    }, 0);
    return () => clearTimeout(timer);
  }, [priceRange]);

  const handleMinPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalRange((prev) => ({ ...prev, min: value }));
    setValidationError(null);
  };

  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalRange((prev) => ({ ...prev, max: value }));
    setValidationError(null);
  };

  const applyFilter = () => {
    const minVal = localRange.min ? parseInt(localRange.min, 10) : null;
    const maxVal = localRange.max ? parseInt(localRange.max, 10) : null;
    if (minVal !== null && (isNaN(minVal) || minVal < MIN_PRICE)) {
      setValidationError(`Min price must be at least ${MIN_PRICE}`);
      return;
    }
    if (maxVal !== null && (isNaN(maxVal) || maxVal > MAX_PRICE)) {
      setValidationError(
        `Max price must be at most ${MAX_PRICE.toLocaleString()}`,
      );
      return;
    }
    if (minVal !== null && maxVal !== null && minVal > maxVal) {
      setValidationError("Min price must be less than or equal to max price");
      return;
    }
    setValidationError(null);
    onPriceRangeChange(localRange);
  };

  const resetFilter = () => {
    const resetRange = { min: "", max: "" };
    setLocalRange(resetRange);
    setValidationError(null);
    onPriceRangeChange(resetRange);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      applyFilter();
    }
  };

  const isFilterActive = priceRange.min !== "" || priceRange.max !== "";

  return (
    <div className="hidden sm:block bg-surface rounded-lg border border-border p-4 shadow-sm">
      <h3 className="text-md font-medium mb-3">Price Range</h3>

      <div className="space-y-3">
        <div className="flex flex-col space-y-2">
          <label
            htmlFor="desktop-min-price"
            className="text-sm text-text-light"
          >
            Min Price (₦)
          </label>
          <input
            id="desktop-min-price"
            type="number"
            min={MIN_PRICE}
            max={MAX_PRICE}
            step={STEP}
            inputMode="numeric"
            className="w-full p-2 border border-border rounded-md focus:ring-primary focus:border-primary"
            placeholder="0"
            value={localRange.min}
            onChange={handleMinPriceChange}
            onKeyDown={handleKeyDown}
            aria-invalid={!!validationError}
          />
        </div>

        <div className="flex flex-col space-y-2">
          <label
            htmlFor="desktop-max-price"
            className="text-sm text-text-light"
          >
            Max Price (₦)
          </label>
          <input
            id="desktop-max-price"
            type="number"
            min={MIN_PRICE}
            max={MAX_PRICE}
            step={STEP}
            inputMode="numeric"
            className="w-full p-2 border border-border rounded-md focus:ring-primary focus:border-primary"
            placeholder="Any"
            value={localRange.max}
            onChange={handleMaxPriceChange}
            onKeyDown={handleKeyDown}
            aria-invalid={!!validationError}
          />
        </div>

        {validationError && (
          <p className="text-sm text-red-600" role="alert">
            {validationError}
          </p>
        )}

        <div className="pt-2 space-y-2">
          <button
            onClick={applyFilter}
            className="w-full py-2 px-4 bg-primary text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            Apply Filter
          </button>

          {isFilterActive && (
            <button
              onClick={resetFilter}
              className="w-full py-2 px-4 border border-border text-foreground rounded-md hover:bg-secondary transition-colors flex items-center justify-center"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear Filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
